/**
 * BRING YOUR PROJECT — the presentation core.
 *
 * Pure and dependency-free, so every rule below is drivable by the test
 * harness without a browser. Nothing here decides whether an import may
 * proceed: the backend decides, this only says what its answer means.
 *
 * THE ONE RULE THAT SHAPES EVERYTHING ELSE
 *
 * `issues` is a plain `list[str]` of English sentences, and EVERY entry is
 * blocking. There is no `severity`, no `code`, no column pointer, and no
 * warning tier — on three of the four preview endpoints there is no
 * non-blocking channel at all. The only warnings in the whole surface are
 * `warnings` / `adjustments` / `unresolvedConstraints` on the REQUIREMENTS
 * COMMIT response, forwarded from the register, and they arrive only after
 * the write.
 *
 * So this module must never invent a severity, and must never soften a
 * blocking issue into a warning to make a file look importable.
 */

/** The backend's own contract version. A mismatch is worth saying out loud. */
export const IMPORT_CONTRACT_VERSION = 'project-import-1';

/** The three kinds, as the backend paths spell them. */
export const IMPORT_KINDS = ['schedule', 'procurement', 'requirements'];

/**
 * Server-side caps, mirrored here ONLY so an operator can be warned before an
 * upload rather than after. The backend re-enforces every one of them on both
 * preview and commit; nothing here is a gate.
 */
export const IMPORT_LIMITS = {
  maxUploadBytes: 5 * 1024 * 1024,
  maxRows: 2000,
  maxRequirementRows: 200,
  maxDependencies: 5000,
  /** Row detail in every response is capped at 200 while counts stay full. */
  maxDetailRows: 200,
};

/**
 * The eight first-class states of an import attempt. A state is a fact about
 * where the operator is, not a style — the dialog renders a different action
 * for each, and `IMPORT_FAILED` is deliberately distinct from
 * `BLOCKING_ISSUES` because one is the file's fault and one is not.
 */
export const IMPORT_STATES = {
  READY_TO_IMPORT: 'READY_TO_IMPORT',
  WARNINGS: 'WARNINGS',
  BLOCKING_ISSUES: 'BLOCKING_ISSUES',
  PREVIEW_CHANGED: 'PREVIEW_CHANGED',
  NO_ACCESS: 'NO_ACCESS',
  NOT_ENABLED_FOR_COMPANY: 'NOT_ENABLED_FOR_COMPANY',
  FEATURE_DISABLED: 'FEATURE_DISABLED',
  IMPORT_FAILED: 'IMPORT_FAILED',
};

const STATE_DISPLAY = {
  READY_TO_IMPORT: { label: 'Ready to import', tone: 'good' },
  WARNINGS: { label: 'Imported with notes', tone: 'attention' },
  BLOCKING_ISSUES: { label: 'Blocking issues', tone: 'bad' },
  PREVIEW_CHANGED: { label: 'File changed since preview', tone: 'attention' },
  NO_ACCESS: { label: 'Not your permission', tone: 'unknown' },
  NOT_ENABLED_FOR_COMPANY: { label: 'Not enabled for this company', tone: 'unknown' },
  FEATURE_DISABLED: { label: 'Turned off on this platform', tone: 'unknown' },
  IMPORT_FAILED: { label: 'Import failed', tone: 'bad' },
};

export const importStateDisplay = (state) =>
  STATE_DISPLAY[state] || { label: String(state || 'Unknown'), tone: 'unknown' };

/**
 * What each kind is, in the operator's language, plus the availability it
 * actually carries. Only requirements is entitlement-gated; saying otherwise
 * would have an operator chasing a grant they do not need.
 */
export const IMPORT_KIND_DISPLAY = {
  schedule: {
    label: 'Schedule',
    describes: 'Activities, planned dates, and the dependencies between them.',
    entitlement: null,
    /** Roles the backend accepts, for explaining a 403 rather than gating. */
    roles: ['owner', 'admin', 'project_manager', 'pm', 'superintendent'],
    rowCap: 2000,
  },
  procurement: {
    label: 'Procurement',
    describes: 'Long-lead items, quantities, vendors, and delivery dates.',
    entitlement: null,
    roles: ['owner', 'admin', 'project_manager', 'pm', 'estimator'],
    rowCap: 2000,
  },
  requirements: {
    label: 'Requirements',
    describes: 'Submittals, permits, inspections and the obligations behind them.',
    entitlement: 'requirements_intelligence',
    roles: ['owner', 'admin', 'project_manager', 'pm', 'superintendent'],
    rowCap: 200,
  },
};

export const importKindDisplay = (kind) =>
  IMPORT_KIND_DISPLAY[kind] || {
    label: String(kind || 'Unknown'),
    describes: '',
    entitlement: null,
    roles: [],
    rowCap: 2000,
  };

/**
 * The columns each kind reads, in the file's own snake_case, so the dialog
 * can tell an operator what to put in the spreadsheet BEFORE they upload.
 *
 * `required` is the backend's own required-ness, not a guess:
 *   schedule      `name` is required; everything else is optional.
 *   procurement   `name` is required.
 *   requirements  a row must carry `spec_section` OR `reference` — a type
 *                 alone cannot key an obligation and the register refuses it.
 */
export const IMPORT_COLUMNS = {
  schedule: [
    { column: 'name', required: true, describes: 'Activity name. Must not already exist.' },
    { column: 'wbs_code', required: false, describes: 'WBS code. Must not already exist.' },
    { column: 'activity_type', required: false, describes: 'phase, milestone, task or summary.' },
    { column: 'status', required: false, describes: 'not_started, in_progress, complete or blocked.' },
    { column: 'start_date_planned', required: false, describes: 'Planned start.' },
    { column: 'end_date_planned', required: false, describes: 'Planned finish.' },
    { column: 'duration_days', required: false, describes: 'Whole days, zero or more.' },
    { column: 'parent_wbs', required: false, describes: 'Parent activity, BY WBS CODE ONLY.' },
    {
      column: 'predecessors',
      required: false,
      describes: 'Semicolon-separated. Each is REF, REF:TYPE or REF:TYPE:LAG — e.g. "1.1:SS:-2".',
    },
  ],
  procurement: [
    { column: 'name', required: true, describes: 'Item name. Must not already exist.' },
    { column: 'category', required: false, describes: 'Free text, up to 100 characters.' },
    { column: 'quantity', required: false, describes: 'Blank means one, not zero.' },
    { column: 'unit', required: false, describes: 'EA, LF, TON…' },
    { column: 'unit_cost', required: false, describes: 'Currency accepted; blank means zero.' },
    { column: 'status', required: false, describes: 'planned, quoted, ordered, shipped, delivered, received, inspected, staged or canceled. Lower case.' },
    { column: 'lead_time_days', required: false, describes: 'Whole days.' },
    { column: 'order_date', required: false, describes: 'When it was or will be ordered.' },
    { column: 'expected_delivery', required: false, describes: 'Expected on site.' },
    { column: 'actual_delivery', required: false, describes: 'Actually on site.' },
    { column: 'vendor_name', required: false, describes: 'Must already exist in this company. Never created by an import.' },
    { column: 'activity_ref', required: false, describes: 'An activity on this project, by WBS code or exact name.' },
  ],
  requirements: [
    { column: 'requirement_type', required: true, describes: 'SUBMITTAL, PERMIT, INSPECTION…' },
    { column: 'title', required: true, describes: 'What the obligation is.' },
    { column: 'spec_section', required: false, describes: 'Spec section. This or reference is required.' },
    { column: 'reference', required: false, describes: 'Drawing or contract reference. This or spec_section is required.' },
    { column: 'description', required: false, describes: 'Detail.' },
    { column: 'required_date', required: false, describes: 'When it must be satisfied.' },
    { column: 'preparation_days', required: false, describes: 'Days to prepare.' },
    { column: 'review_days', required: false, describes: 'Days for review.' },
    { column: 'resubmittal_review_days', required: false, describes: 'Days for a re-review.' },
    { column: 'review_cycles_allowed', required: false, describes: 'How many review rounds.' },
    { column: 'responsible_party', required: false, describes: 'Who owes it.' },
    { column: 'government_authority', required: false, describes: 'The AHJ, where one applies.' },
    {
      column: 'verification_status',
      required: false,
      describes:
        'Only INFERRED or OPERATOR_ENTERED are accepted. Blank becomes OPERATOR_ENTERED — an honest statement that a person typed it, not evidence.',
    },
    { column: 'activity_ref', required: false, describes: 'An activity on this project, by WBS code or exact name.' },
  ],
};

export const importColumns = (kind) => IMPORT_COLUMNS[kind] || [];

/**
 * The provenance an imported requirement actually carries.
 *
 * A CSV cannot cite a document — no column feeds `sourceDocumentId` or
 * `sourceQuote`, so no imported row can reach DOCUMENT_CITED at import time.
 * The register puts OPERATOR_ENTERED in its own WEAK_VERIFICATION set, which
 * makes an imported hard gate REPORT_ONLY rather than BLOCKING.
 *
 * Saying "verified" here would silently promote a spreadsheet into
 * contractual evidence, so the copy says the opposite, plainly.
 */
export const IMPORTED_PROVENANCE = {
  status: 'OPERATOR_ENTERED',
  label: 'Operator entered',
  tone: 'unknown',
  meaning:
    'A person typed this in. It is honest provenance and it is not evidence — no imported row is document-cited or verified, because a spreadsheet carries no citation.',
  consequence:
    'A mandatory requirement entered this way reports; it does not block work until a document or a human verification elevates it.',
};

/**
 * Provenance for one previewed requirement row. A row that names a
 * verification the register would accept is echoed as that, never upgraded;
 * a blank one is OPERATOR_ENTERED, and nothing here elevates anything.
 */
export const presentImportedProvenance = (parsed) => {
  /**
   * The cell is `.upper()`-normalized by the backend and then constrained to
   * `Literal["INFERRED", "OPERATOR_ENTERED"] | None` by the register's own
   * schema — a file may say "this was derived from context" or "a person
   * typed this", and nothing stronger. Anything else is a row issue, so the
   * only two values that can reach here are both weak.
   */
  const declared = String(parsed?.verificationStatus || '').toUpperCase();
  if (!declared || declared === 'OPERATOR_ENTERED') return IMPORTED_PROVENANCE;
  /**
   * Anything other than INFERRED is a row the register will refuse, so
   * echoing it as a badge would show DOCUMENT_CITED or HUMAN_VERIFIED beside
   * a row that cannot be imported — the exact promotion this surface exists
   * to prevent. A refused value reports as what it is: not accepted.
   */
  if (declared !== 'INFERRED') {
    return {
      status: declared,
      label: 'Not accepted',
      tone: 'unknown',
      meaning:
        'A file may only declare INFERRED or OPERATOR_ENTERED. Anything stronger is a claim of evidence a spreadsheet cannot carry, and the register refuses the row.',
      consequence: IMPORTED_PROVENANCE.consequence,
    };
  }
  return {
    status: declared,
    label: declared.replace(/_/g, ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase()),
    tone: 'unknown',
    meaning:
      'Declared in the file. Still weak provenance — it reports, it does not enforce, and an import never upgrades it.',
    consequence: IMPORTED_PROVENANCE.consequence,
  };
};

// ---------------------------------------------------------------------------
// The preview
// ---------------------------------------------------------------------------

const asRows = (payload) => (Array.isArray(payload?.rows) ? payload.rows : []);

/**
 * The preview, summarized.
 *
 * Two truths this must keep apart, because the backend does:
 *
 *   `rows` is capped at 200 while `totalRows` / `rowsWithIssues` count the
 *   WHOLE file, and there is no `truncated` flag. The only way to know rows
 *   were dropped is to compare the two — so that comparison is done here,
 *   once, rather than left to each caller to forget.
 *
 *   `readyToCommit` is `bool(results) and with_issues == 0` — an EMPTY file
 *   is 0 rows, 0 issues, and NOT ready. Keying a Commit button on
 *   `rowsWithIssues === 0` alone would enable it for a file the commit
 *   refuses with `nothing_to_import`.
 */
export const summarizePreview = (payload) => {
  if (!payload) return null;
  const rows = asRows(payload);
  const totalRows = payload.totalRows || 0;
  const rowsWithIssues = payload.rowsWithIssues || 0;

  return {
    contractVersion: payload.contractVersion || null,
    contractMatches: payload.contractVersion === IMPORT_CONTRACT_VERSION,
    projectId: payload.projectId || null,
    kind: payload.kind || null,
    /** The value the commit must echo back, under the name the WIRE uses. */
    fileSha256: payload.fileSha256 || null,
    totalRows,
    validRows: payload.validRows || 0,
    rowsWithIssues,
    columnsFound: Array.isArray(payload.columnsFound) ? payload.columnsFound : [],
    rows,
    /** The backend's own verdict. Never recomputed here. */
    readyToCommit: payload.readyToCommit === true,
    /** Rows are capped at 200; the counts are not. */
    rowsTruncated: rows.length < totalRows,
    shownRows: rows.length,
    /** An empty file is its own state, distinct from a clean one. */
    isEmpty: totalRows === 0,
  };
};

/**
 * Which of the eight states a previewed file is in.
 *
 * Note the deliberate absence of WARNINGS: no preview endpoint has a
 * non-blocking channel, so a preview can never land there. WARNINGS is
 * reachable only from a requirements COMMIT result.
 */
export const previewState = (summary) => {
  if (!summary) return null;
  if (summary.isEmpty) return IMPORT_STATES.BLOCKING_ISSUES;
  if (summary.rowsWithIssues > 0) return IMPORT_STATES.BLOCKING_ISSUES;
  if (summary.readyToCommit) return IMPORT_STATES.READY_TO_IMPORT;
  // readyToCommit false with no counted issues is a backend answer this
  // client does not model; report it rather than guessing it is fine.
  return IMPORT_STATES.BLOCKING_ISSUES;
};

/**
 * Whether the Import button may be offered at all.
 *
 * It follows the backend's `readyToCommit` and nothing else — not a row
 * count, not an issue count. Deriving it independently is how a client ends
 * up enabling a button the server refuses.
 */
export const canCommit = (summary) => Boolean(summary && summary.readyToCommit);

/**
 * Every blocking issue, flattened, with its row still attached.
 *
 * The issue strings do NOT repeat the row number — an issue lifted out of its
 * row object loses all positional context — so the row travels with it.
 *
 * `rowNumber` is 1-based over the DATA rows the reader KEPT, and both readers
 * skip entirely blank rows. So `rowNumber + 1` is the spreadsheet line only
 * for a file with no blank rows above the one in question; with blank rows it
 * drifts by however many were skipped. It is offered as a starting point and
 * the caller says "about", because an exact promise here would send an
 * operator to the wrong line with confidence.
 */
export const flattenIssues = (payload) => {
  const out = [];
  asRows(payload).forEach((row) => {
    (row?.issues || []).forEach((issue) => {
      out.push({
        rowNumber: row.rowNumber ?? null,
        spreadsheetLine: typeof row.rowNumber === 'number' ? row.rowNumber + 1 : null,
        issue: String(issue),
        parsed: row.parsed || null,
      });
    });
  });
  return out;
};

/**
 * Group identical issues so a file with one wrong column does not present as
 * 400 separate problems. The row numbers are kept, because "which rows" is
 * the question an operator asks next.
 *
 * WHAT THIS CAN AND CANNOT SEE. `rows` is capped at 200 by the backend while
 * `rowsWithIssues` counts the whole file, so these groups are computed over
 * the visible 200 ONLY. A file whose first 200 rows are clean and whose 300th
 * is broken yields ZERO groups beside a non-zero `rowsWithIssues` — the
 * caller must read `issueRowsShown` against the summary's count and say so,
 * or an operator sees "1 row has blocking issues" with nothing listed and no
 * explanation.
 */
export const groupIssues = (payload) => {
  const groups = new Map();
  flattenIssues(payload).forEach((entry) => {
    const key = entry.issue;
    if (!groups.has(key)) groups.set(key, { issue: key, rows: [], count: 0 });
    const group = groups.get(key);
    group.count += 1;
    if (group.rows.length < 12) group.rows.push(entry.rowNumber);
  });
  return [...groups.values()].sort((a, b) => b.count - a.count || a.issue.localeCompare(b.issue));
};

/**
 * How much of the file's trouble the operator can actually see.
 *
 * `shown` counts the rows WITH ISSUES inside the returned 200; `total` is the
 * backend's count over the whole file. When they differ — including the case
 * where `shown` is 0 and `total` is not — the screen has to say so rather
 * than present an empty list as an empty problem.
 */
export const issueVisibility = (payload) => {
  const rows = asRows(payload);
  const shown = rows.filter((row) => (row?.issues || []).length > 0).length;
  const total = payload?.rowsWithIssues || 0;
  return {
    shown,
    total,
    hidden: Math.max(total - shown, 0),
    complete: shown === total,
    /** The worst case: trouble exists and NONE of it is listed. */
    noneVisible: total > 0 && shown === 0,
  };
};

// ---------------------------------------------------------------------------
// Refusals
// ---------------------------------------------------------------------------

/**
 * Turn an import refusal into something an operator can act on.
 *
 * Takes an already-classified workflow error (see lib/workflowErrors) so the
 * layered availability vocabulary is not re-implemented here. What this adds
 * is the import-specific codes and, for each, whether the answer is "fix the
 * file", "preview again", or "ask someone".
 */
export const presentImportError = (classified, kind) => {
  const code = classified?.code || null;
  const detail = classified?.detail || null;
  const display = importKindDisplay(kind);

  if (code === 'preview_mismatch') {
    return {
      state: IMPORT_STATES.PREVIEW_CHANGED,
      title: 'The file no longer matches the version you previewed',
      explanation:
        'The file no longer matches the version you previewed. Preview it again before importing.',
      detail:
        'OMNΛS confirms an import by the exact bytes it was shown. Something changed between the preview and now — a re-save, an edit, a different copy of the file — so nothing was imported.',
      /** Never retried automatically: a stale confirmation is the whole point. */
      retryable: false,
      action: 'preview-again',
      rows: [],
    };
  }

  if (code === 'rows_with_issues') {
    const rows = Array.isArray(detail?.rows) ? detail.rows : [];
    return {
      state: IMPORT_STATES.BLOCKING_ISSUES,
      title: 'Nothing was imported',
      explanation: classified.message,
      detail:
        'An import is all-or-nothing. One unusable row refuses the whole file, so a project is never half-loaded.',
      retryable: false,
      action: 'fix-file',
      rows,
      rowsTruncated: rows.length >= 200,
    };
  }

  if (code === 'nothing_to_import') {
    return {
      state: IMPORT_STATES.BLOCKING_ISSUES,
      title: 'The file has no data rows',
      explanation: classified.message,
      detail: 'Column headings alone are not an import. Add at least one row beneath them.',
      retryable: false,
      action: 'fix-file',
      rows: [],
    };
  }

  if (code === 'too_many_rows') {
    return {
      state: IMPORT_STATES.BLOCKING_ISSUES,
      title: 'The file is too large to import in one go',
      explanation: classified.message,
      detail: `This import accepts at most ${display.rowCap} rows per file. Split it and import the parts in order.`,
      retryable: false,
      action: 'fix-file',
      rows: [],
    };
  }

  if (code === 'too_many_dependencies') {
    return {
      state: IMPORT_STATES.BLOCKING_ISSUES,
      title: 'The file plans too many dependencies',
      explanation: classified.message,
      detail:
        'The dependency cap is reached while the file is still being read, so OMNΛS cannot say which rows are responsible. Split the schedule and import it in parts.',
      retryable: false,
      action: 'fix-file',
      rows: [],
    };
  }

  if (code === 'file_too_large') {
    return {
      state: IMPORT_STATES.BLOCKING_ISSUES,
      title: 'The file is too large',
      explanation: classified.message,
      detail: 'This import accepts files up to 5 MB.',
      retryable: false,
      action: 'fix-file',
      rows: [],
    };
  }

  if (code === 'insert_only_violated') {
    return {
      state: IMPORT_STATES.IMPORT_FAILED,
      title: 'The import was stopped to protect existing records',
      explanation: classified.message,
      detail:
        'An import may only add. This one would have changed a requirement that already existed, so OMNΛS refused it rather than overwrite work someone else recorded. Nothing was changed. This is worth reporting.',
      retryable: false,
      action: 'report',
      rows: [],
    };
  }

  if (code === 'unresolvable_link' || code === 'invalid_requirement') {
    return {
      state: IMPORT_STATES.BLOCKING_ISSUES,
      title: 'A row points at something that is not there',
      explanation: classified.message,
      detail: detail?.field ? `The register could not resolve ${detail.field}.` : null,
      retryable: false,
      action: 'fix-file',
      rows: [],
    };
  }

  // The layered availability refusals, which are properties of the SURFACE
  // rather than of the file. Only the requirements kind can reach the middle
  // two at all.
  if (classified?.kind === 'disabled') {
    return {
      state: IMPORT_STATES.FEATURE_DISABLED,
      title: 'This import is turned off on this platform',
      explanation: classified.message,
      detail: 'Nothing about your file or your permissions caused this.',
      retryable: false,
      action: 'wait',
      rows: [],
    };
  }
  if (classified?.kind === 'not-entitled') {
    return {
      state: IMPORT_STATES.NOT_ENABLED_FOR_COMPANY,
      title: 'Your company has not been granted this yet',
      explanation: classified.message,
      detail: display.entitlement
        ? `${display.label} import needs the ${display.entitlement} grant. It is granted by the platform, not self-served.`
        : 'This grant is made by the platform, not self-served.',
      retryable: false,
      action: 'ask-platform',
      rows: [],
    };
  }
  if (classified?.kind === 'no-access') {
    return {
      state: IMPORT_STATES.NO_ACCESS,
      title: 'Your role may not import this',
      explanation: classified.message,
      detail: `${display.label} import is open to ${display.roles
        .filter((r) => r !== 'pm')
        .join(', ')
        .replace(/_/g, ' ')}.`,
      retryable: false,
      action: 'ask-admin',
      rows: [],
    };
  }
  if (classified?.kind === 'not-found') {
    return {
      state: IMPORT_STATES.IMPORT_FAILED,
      title: 'This project could not be found',
      explanation: classified.message,
      detail: 'It may have been removed, or it may belong to another company.',
      retryable: false,
      action: 'reload',
      rows: [],
    };
  }
  if (classified?.kind === 'network') {
    return {
      state: IMPORT_STATES.IMPORT_FAILED,
      title: 'Could not reach the server',
      explanation: classified.message,
      // A commit whose response never arrived may or may not have landed.
      detail:
        'If this happened while importing, check the project before trying again — the import may have completed even though the answer did not come back.',
      retryable: true,
      action: 'retry',
      rows: [],
    };
  }

  /**
   * Three refusals arrive as a BARE STRING rather than {code, message}: the
   * 400 for an unreadable .xlsx, the 403 role refusal, and the 404. The
   * classifier already handles both shapes; what is left here is the
   * unreadable-file case, which has no code to switch on.
   */
  if (classified?.status === 400) {
    return {
      state: IMPORT_STATES.BLOCKING_ISSUES,
      title: 'That file could not be read',
      explanation: classified.message,
      detail:
        'OMNΛS chooses how to read a file by its name alone. A .xlsx name must be a real Excel workbook; anything else is read as CSV.',
      retryable: false,
      action: 'fix-file',
      rows: [],
    };
  }

  return {
    state: IMPORT_STATES.IMPORT_FAILED,
    title: 'The import failed',
    explanation: classified?.message || 'Request failed.',
    detail: null,
    retryable: true,
    action: 'retry',
    rows: [],
  };
};

// ---------------------------------------------------------------------------
// The result
// ---------------------------------------------------------------------------

/**
 * What actually landed.
 *
 * The three kinds return three different shapes, and flattening them would
 * lose the two facts worth reporting: schedule creates dependencies as well
 * as activities, and requirements is the only kind that returns the
 * register's own honesty payload.
 *
 * `updated` is structurally always 0 on a 200 — a non-zero value raises the
 * insert_only_violated tripwire first — so it is reported as a reassurance
 * that nothing was overwritten, not as a count of work done.
 */
export const presentImportResult = (payload, kind) => {
  if (!payload) return null;
  const warnings = Array.isArray(payload.warnings) ? payload.warnings : [];
  const adjustments = Array.isArray(payload.adjustments) ? payload.adjustments : [];
  const unresolved = Array.isArray(payload.unresolvedConstraints)
    ? payload.unresolvedConstraints
    : [];
  const hasNotes = warnings.length + adjustments.length + unresolved.length > 0;

  const created = payload.created || 0;
  const lines = [`${created} ${created === 1 ? 'record' : 'records'} added`];
  if (typeof payload.dependenciesCreated === 'number') {
    lines.push(
      `${payload.dependenciesCreated} ${
        payload.dependenciesCreated === 1 ? 'dependency' : 'dependencies'
      } linked`
    );
  }

  return {
    kind: payload.kind || kind || null,
    created,
    dependenciesCreated:
      typeof payload.dependenciesCreated === 'number' ? payload.dependenciesCreated : null,
    /** Always 0 on success. Reported as "nothing was overwritten". */
    updated: payload.updated || 0,
    fileSha256: payload.fileSha256 || null,
    headline: lines.join(' · '),
    warnings,
    adjustments,
    unresolvedConstraints: unresolved,
    hasNotes,
    /**
     * The one place WARNINGS is reachable. A requirements import that
     * succeeded but could not record a review duration is not a failure and
     * must not read as one — but the note is the operator's to see.
     */
    state: hasNotes ? IMPORT_STATES.WARNINGS : IMPORT_STATES.READY_TO_IMPORT,
    /** Every imported requirement is operator-entered. Said once, here. */
    provenanceNote: (payload.kind || kind) === 'requirements' ? IMPORTED_PROVENANCE : null,
  };
};

// ---------------------------------------------------------------------------
// Row rendering
// ---------------------------------------------------------------------------

const cell = (label, value) => ({ label, value: value === undefined ? null : value });

/**
 * The fields worth showing for one previewed row, per kind, in construction
 * order rather than payload order.
 *
 * A null is rendered as unknown by the caller, never as a zero or a blank —
 * the parsed echo distinguishes "the cell was empty" from "the cell said 0",
 * and that distinction survives to the screen.
 */
export const presentPreviewRow = (row, kind) => {
  const parsed = row?.parsed || {};
  const base = {
    rowNumber: row?.rowNumber ?? null,
    spreadsheetLine: typeof row?.rowNumber === 'number' ? row.rowNumber + 1 : null,
    issues: Array.isArray(row?.issues) ? row.issues : [],
    hasIssues: (row?.issues || []).length > 0,
  };

  if (kind === 'schedule') {
    const deps = Array.isArray(parsed.dependencies) ? parsed.dependencies : [];
    return {
      ...base,
      title: parsed.name || null,
      cells: [
        cell('WBS', parsed.wbsCode),
        cell('Type', parsed.activityType),
        cell('Status', parsed.status),
        cell('Planned start', parsed.startDatePlanned),
        cell('Planned finish', parsed.endDatePlanned),
        cell('Duration', typeof parsed.durationDays === 'number' ? `${parsed.durationDays}d` : null),
      ],
      /**
       * Dependencies carry no row pointer and no successor — the successor is
       * implicitly this row — so they are rendered as this row's, never as
       * free-standing edges.
       *
       * THE IDS ARE DELIBERATELY DROPPED. `file_ids = [generate_id() for _ in
       * rows]` is regenerated on EVERY call, and preview and commit are
       * independent invocations, so a `predecessorId` or `parentActivityId`
       * from a preview is a throwaway value that will NOT equal the id the
       * commit writes. Showing one would put a meaningless identifier in
       * front of an operator and invite a client to round-trip it. What is
       * true and useful — that the reference resolved, and to what kind of
       * link — is kept.
       */
      dependencies: deps.map((dep) => ({
        dependencyType: dep?.dependencyType || null,
        lagDays: typeof dep?.lagDays === 'number' ? dep.lagDays : null,
        /** It resolved, which is the only durable fact about it here. */
        resolved: Boolean(dep?.predecessorId),
      })),
      dependencyCount: deps.length,
      /** That a parent resolved — never the throwaway id it resolved to. */
      parentResolved: Boolean(parsed.parentActivityId),
    };
  }

  if (kind === 'procurement') {
    return {
      ...base,
      title: parsed.name || null,
      cells: [
        cell('Category', parsed.category),
        cell(
          'Quantity',
          typeof parsed.quantity === 'number'
            ? `${parsed.quantity}${parsed.unit ? ` ${parsed.unit}` : ''}`
            : null
        ),
        cell('Status', parsed.status),
        cell('Vendor', parsed.vendorName),
        cell('Expected delivery', parsed.expectedDelivery),
        cell('Lead time', typeof parsed.leadTimeDays === 'number' ? `${parsed.leadTimeDays}d` : null),
      ],
      /**
       * A vendor NAME with no id means the row named a firm this company does
       * not have — which is a blocking issue, never a vendor to create here.
       */
      vendorResolved: Boolean(parsed.vendorId),
      vendorNamed: Boolean(parsed.vendorName),
      activityLinked: Boolean(parsed.activityId),
      dependencies: [],
    };
  }

  if (kind === 'requirements') {
    return {
      ...base,
      title: parsed.title || parsed.specSection || parsed.reference || null,
      cells: [
        cell('Kind', parsed.requirementType),
        cell('Spec section', parsed.specSection),
        cell('Reference', parsed.reference),
        cell('Required by', parsed.requiredDate),
        cell('Review days', parsed.reviewDays),
        cell('Responsible', parsed.responsibleParty),
      ],
      provenance: presentImportedProvenance(parsed),
      activityLinked: Boolean(parsed.activityId),
      dependencies: [],
    };
  }

  return { ...base, title: null, cells: [], dependencies: [] };
};

/**
 * A file's own facts, checked before it is sent.
 *
 * This is courtesy, not a gate — the backend re-checks all of it — so it
 * refuses nothing and only warns. Format dispatch on the server is by
 * FILENAME ALONE, which is worth saying because a .xlsx named .csv silently
 * produces garbage rows rather than an error.
 */
export const describeChosenFile = (file, kind) => {
  if (!file) return null;
  const name = file.name || '';
  const lower = name.toLowerCase();
  const isXlsx = lower.endsWith('.xlsx');
  const isCsv = lower.endsWith('.csv');
  const notes = [];

  if (file.size > IMPORT_LIMITS.maxUploadBytes) {
    notes.push('This file is larger than 5 MB and will be refused.');
  }
  if (!isXlsx && !isCsv) {
    notes.push(
      'Only .csv and .xlsx are read. OMNΛS chooses by the file name alone, so anything else is read as CSV and will probably produce nonsense rows.'
    );
  }
  if (isXlsx) {
    notes.push('Only the first worksheet of a workbook is read.');
  }
  if (isCsv) {
    /**
     * The XLSX reader strips its header cells; the CSV reader takes
     * `csv.DictReader.fieldnames` verbatim. So a CSV whose heading row is
     * " name,wbs_code" yields the key " name", every row reports a missing
     * name, and the byte-identical workbook imports cleanly. It is the one
     * failure whose cause is invisible in the file.
     */
    notes.push('Column headings must have no spaces around them — " name" is not "name".');
  }

  return {
    name,
    size: file.size || 0,
    format: isXlsx ? 'xlsx' : 'csv',
    rowCap: importKindDisplay(kind).rowCap,
    notes,
  };
};
